import clsx from "clsx";
import { Item } from "gamedata/GameData";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { Input } from "~/components/form/Input";
import { AdenaValue } from "~/components/item/Adena";
import { ItemLink } from "~/components/item/ItemLink";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Icon32 } from "~/components/ui/Icon";
import { Table } from "~/components/ui/Table";
import { Realm, tt, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE } from "~/util/Loaders";
import css from "./calc.module.css";
import { gameName } from "~/util/common";
import { homePage } from "~/util/titles";
import { logPageView } from "~/util/stats";

const PRICES_STORAGE = "prices";

function getItemsName(targets: CraftTarget[], realm: Realm) {
  const t = tt.bind(null, realm.lang);
  const namesMap = new Map<number, string>();

  for (const target of targets) {
    namesMap.set(target.item.id, target.item.name);
  }

  return Array.from(namesMap.values()).join(", ");
}

$PAGE({
  path: "craft/calc",
  meta: (args) => {
    const t = args.t;
    return {
      title: `${t({
        en: "Craft Calculator",
        ru: "Калькулятор крафта",
      })} (${getItemsName(args.data.targets, args.realm)}) - ${homePage(
        args.realm
      )}`,
      description: getDescription({ realm: args.realm }),
      ogTitle:
        args.data.targets.length > 0
          ? `${t({
              en: "Craft Calculator",
              ru: "Калькулятор крафта",
            })}: ${args.data.targets.map((t) => [t.item.name]).join(", ")}`
          : t({ en: "Craft Calculator", ru: "Калькулятор крафта" }),
      ogImage:
        args.data.targets.length > 0
          ? `https://l2hub.info/s/icons-gf/${args.data.targets[0].item.icon}.png`
          : null,
      canonicalUrl: `/craft/calc`,
    };
  },
  loader: $LOADER?.(async (args) => {
    // Log
    logPageView(args);
    //
    const t = tt.bind(null, args.realm.lang);
    const itemIds = parseItemsParam(args.searchParams);

    const itemsMap = new Map<number, ItemData>();
    function collectItemInfo(item: Item, recipeId?: number) {
      if (itemsMap.has(item.id)) {
        return;
      }
      const itemData: ItemData = {
        id: item.id,
        itemName: item.itemName,
        name: t(item.name),
        icon: item.icon,
        defaultPrice: item.defaultPrice ?? 0,
        recipes: [],
      };
      itemsMap.set(item.id, itemData);
      const product = recipeId
        ? item.product.find((p) => p.recipe.id === recipeId)
        : item.product[0];
      if (product) {
        itemData.recipes.push({
          mats: product.recipe.materialList.map((m) => ({
            itemId: m.item.id,
            count: m.count,
          })),
          prod: product.recipe.productList.map((p) => ({
            itemId: p.item.id,
            count: p.count,
          })),
        });
        for (const mat of product.recipe.materialList) {
          collectItemInfo(mat.item);
        }
      }
    }
    const targets: CraftTarget[] = [];
    itemIds.forEach((id) => {
      const recipeItem = args.gd.itemsById.get(id);
      const recipe = recipeItem?.recipe[0];
      if (recipe) {
        const product = recipe.productList.at(0);
        if (product) {
          targets.push({
            item: {
              id: product.item.id,
              itemName: product.item.itemName,
              name: t(product.item.name),
              icon: product.item.icon,
            },
            recipe: {
              mats: recipe.materialList.map((mat) => ({
                itemId: mat.item.id,
                count: mat.count,
              })),
              prod: product.recipe.productList.map((p) => ({
                itemId: p.item.id,
                count: p.count,
              })),
            },
            rate: product.recipe.successRate,
          });
          collectItemInfo(product.item, recipe.id);
        }
      }
    });

    return {
      authUser: args.req.sess?.user,
      targets,
      items: Array.from(itemsMap.values()),
    };
  }),
  shouldRevalidate: (args) => {
    const cur = args.currentUrl;
    const next = args.nextUrl;
    return (
      cur.pathname !== cur.pathname ||
      cur.searchParams.get("items") !== next.searchParams.get("items")
    );
  },
  render: (args) => {
    const t = useT();
    const turl = useTurl();
    const breadcrumbList = [
      {
        title: t({ en: "Craft Calculator", ru: "Калькулятор Крафта" }),
        url: turl("/craft/calc"),
      },
    ];

    return (
      <div>
        <div className="content">
          <Breadcrumb items={breadcrumbList} />
          <div className="columns">
            <CraftCalc
              targets={args.data.targets}
              itemsData={args.data.items}
            />
          </div>
        </div>
      </div>
    );
  },
});

type CraftTarget = {
  item: { id: number; itemName: string; name: string; icon: string };
  recipe: RecipeData;
  rate: number | null;
};

type ItemData = {
  id: number;
  itemName: string;
  name: string;
  icon: string;
  defaultPrice: number;
  recipes: RecipeData[];
};

type RecipeData = {
  mats: Array<{ itemId: number; count: number }>;
  prod: Array<{ itemId: number; count: number }>;
};

type CalcState = {
  craftCountByPath: Record<string, number>;
  avails: Record<number, number>;
  prices: Record<number, number>;
};

function CraftCalc(props: { targets: CraftTarget[]; itemsData: ItemData[] }) {
  const itemMap = useMemo(
    () => new Map(props.itemsData.map((x) => [x.id, x])),
    [props.itemsData]
  );

  // подгружаем цены из localStorage
  const [pricesStorage, setPricesStorage] = useState<Record<number, number>>(
    {}
  );
  useEffect(() => {
    const prices = localStorage.getItem(PRICES_STORAGE);
    if (prices) {
      const records: Record<number, string> = JSON.parse(prices);
      setPricesStorage(convertRecord(records));
    }
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const loc = useLocation();
  const state = useMemo(() => {
    const state: CalcState = {
      craftCountByPath: {},
      avails: {},
      prices: {},
    };

    state.craftCountByPath = decodeRecParam(
      searchParams.get("c") || "",
      (k) => k,
      (v) => parseInt(v)
    );
    state.avails = decodeRecParam(
      searchParams.get("a") || "",
      (k) => parseInt(k),
      (v) => parseInt(v)
    );

    state.prices = {
      ...filterRecord(
        pricesStorage,
        props.itemsData.map((i) => i.id)
      ),
      ...decodeRecParam(
        searchParams.get("p") || "",
        (k) => parseInt(k),
        (v) => parseInt(v)
      ),
    };

    return normalizeState(state, props.targets, itemMap);
  }, [loc.search, pricesStorage, setPricesStorage]);

  function setState(action: SetStateAction<CalcState>) {
    const s_ = typeof action === "function" ? action(state) : action;
    const s = normalizeState(s_, props.targets, itemMap);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("c", encodeRecParam(s.craftCountByPath));
    nextParams.set("a", encodeRecParam(s.avails));
    nextParams.set("p", encodeRecParam(s.prices));
    setSearchParams(nextParams, { preventScrollReset: true });
  }

  return (
    <div className={css.root}>
      <div style={{ flex: "0 0 340px" }}>
        <div className={css.columnTitle}>
          <div className={css.columnTitleText}>Items to craft</div>
        </div>
        <div className={css.targets}>
          {props.targets.map((target) => {
            return (
              <ItemAndRecipe
                key={target.item.id}
                itemId={target.item.id}
                map={itemMap}
                state={state}
                setState={setState}
              />
            );
          })}
        </div>
      </div>
      <div style={{ flex: "1 1" }}>
        <ResourcesTable
          targets={props.targets}
          map={itemMap}
          state={state}
          pricesStorage={pricesStorage}
          setState={setState}
        />
      </div>
    </div>
  );
}

function ResourcesTable(props: {
  targets: CraftTarget[];
  map: Map<number, ItemData>;
  state: CalcState;
  pricesStorage: Record<number, number>;
  setState: (s: SetStateAction<CalcState>) => void;
}) {
  const allMats = new Map<
    number,
    { item: ItemData; total: number; totalPrice: number }
  >();
  let totalPrice = 0;
  const path: number[] = [];
  function collect(itemId: number, parentCount: number, depth = 0) {
    if (depth > 10) {
      return;
    }
    path.push(itemId);
    const item = props.map.get(itemId);
    if (item) {
      const prodCount = item.recipes[0]?.prod[0].count;
      for (const mat of item.recipes[0]?.mats ?? []) {
        const matItem = props.map.get(mat.itemId)!;
        let matInfo = allMats.get(mat.itemId);
        if (!matInfo) {
          matInfo = { item: matItem, total: 0, totalPrice: 0 };
          allMats.set(mat.itemId, matInfo);
        }

        const toCraft =
          props.state.craftCountByPath[path.join(".") + "." + mat.itemId] ?? 0;
        // баг отображения у некоторых ресов, например корд
        // const totalCount = Math.ceil(
        //   mat.count * (parentCount / prodCount) - toCraft
        // );
        // решение на пробу
        const totalCount = Math.ceil(mat.count * parentCount - toCraft);
        //
        matInfo.total += totalCount;

        const price =
          props.state.prices[mat.itemId] ?? matInfo.item.defaultPrice;
        const cost =
          price *
          Math.max(0, totalCount - (props.state.avails[mat.itemId] || 0));
        matInfo.totalPrice += cost;
        totalPrice += cost;

        collect(mat.itemId, toCraft, depth + 1);
      }
    }
    path.pop();
  }
  props.targets.forEach((target) => collect(target.item.id, 1));

  const [avails, setAvails] = useState<Record<number, string>>({});
  const [prices, setPrices] = useState<Record<number, string>>({});

  return (
    <div>
      <div className={css.columnTitle}>
        <div className={css.columnTitleText}>Needed resources</div>
        <div className={css.totalPrice}>
          Total cost: <AdenaValue value={totalPrice} />
        </div>
      </div>
      <Table
        data={Array.from(allMats.values())}
        id={(item) => item.item.id}
        cols={[
          {
            title: "Recource",
            cell: (item) => (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Icon32 icon={item.item.icon} />
                <ItemLink item={item.item} showIcon={false} />
              </div>
            ),
          },
          {
            title: "Required",
            cell: (item) => item.total,
          },
          {
            title: "Available",
            cell: (item) => (
              <Input
                size={5}
                placeholder="0"
                value={
                  avails[item.item.id] ?? props.state.avails[item.item.id] ?? ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  setAvails((avails) => ({ ...avails, [item.item.id]: value }));

                  const cleanValue = value.replace(/[^\d]/g, "");
                  if (cleanValue.length > 0) {
                    const numValue = parseInt(cleanValue) || 0;
                    props.setState((state) => ({
                      ...state,
                      avails: { ...state.avails, [item.item.id]: numValue },
                    }));
                  } else {
                    props.setState((state) => {
                      const avails = { ...state.avails };
                      delete avails[item.item.id];
                      return { ...state, avails };
                    });
                  }
                }}
              />
            ),
          },
          {
            title: "Price",
            cell: (item) => (
              <Input
                size={5}
                placeholder={String(item.item.defaultPrice)}
                value={
                  prices[item.item.id] ?? props.state.prices[item.item.id] ?? ""
                }
                onChange={(e) => {
                  const value = e.target.value;

                  setPrices((prices) => ({ ...prices, [item.item.id]: value }));
                  // добавляем цену в localStorage
                  localStorage.setItem(
                    PRICES_STORAGE,
                    JSON.stringify({
                      ...{ ...props.pricesStorage, ...prices },
                      [item.item.id]: value,
                    })
                  );

                  const cleanValue = value.replace(/[^\d]/g, "");
                  if (cleanValue.length > 0) {
                    const numValue = parseInt(cleanValue) || 0;

                    props.setState((state) => ({
                      ...state,
                      prices: { ...state.prices, [item.item.id]: numValue },
                    }));
                  } else {
                    props.setState((state) => {
                      const prices = { ...state.prices };
                      delete prices[item.item.id];
                      return { ...state, prices };
                    });
                  }
                }}
              />
            ),
          },
          {
            title: "Total Cost",
            cell: (item) => <AdenaValue value={item.totalPrice} />,
          },
        ]}
      />
    </div>
  );
}

function ItemAndRecipe(props: {
  itemId: number;
  map: Map<number, ItemData>;
  state: CalcState;
  setState: (s: SetStateAction<CalcState>) => void;
}) {
  const [openByPath, setOpenByPath] = useState<
    Record<string, boolean | undefined>
  >({});
  const path: number[] = [];
  const item = props.map.get(props.itemId);
  if (item) {
    let materialList = null;
    if (item.recipes.length > 0) {
      path.push(item.id);
      materialList = renderMaterialsLevel(
        props.map,
        item.recipes[0],
        1,
        [props.state, props.setState],
        0
      );
      path.pop();
    }

    return (
      <div key={item.id} className={css.targetBox}>
        <div className={css.targetHead}>
          <div className={css.targetIcon}>
            <Icon32 icon={item.icon} />
          </div>
          <div className={css.targetName}>
            <ItemLink item={item} showIcon={false} />
          </div>
        </div>
        {materialList}
      </div>
    );
  } else {
    return <div key={props.itemId}>Item #{props.itemId}</div>;
  }

  function renderMaterialsLevel(
    map: Map<number, ItemData>,
    recipe: RecipeData,
    parentCount: number,
    [state, setState]: [CalcState, (s: CalcState) => void],
    level: number
  ) {
    return (
      <div style={{ paddingLeft: 6 + level * 10 }}>
        {recipe.mats.map((mat) => {
          const item = map.get(mat.itemId);
          if (!item || level > 10) {
            return null;
          }
          const pathId = `${path.join(".")}.${item.id}`;
          const prodCount = item.recipes[0]?.prod[0].count ?? 1;
          const totalCount = Math.ceil(mat.count * parentCount);
          let countSelector = null;
          let children = null;
          if (item.recipes[0]) {
            const craftCount = Math.min(
              state.craftCountByPath[pathId] ?? 0,
              totalCount
            );
            countSelector = (
              <div>
                <div className={css.recipeCraftCountValues}>
                  <div>
                    need:{" "}
                    <span className={css.recipeCraftCountVal}>
                      {totalCount}
                    </span>
                  </div>
                  <div>
                    <span className={css.recipeCraftCountVal}>
                      {craftCount}
                    </span>{" "}
                    to craft
                  </div>
                </div>
                <div className={css.rangeWrapper}>
                  <input
                    className={clsx(
                      css.range,
                      totalCount == 0 && css.rangeDisabled
                    )}
                    type="range"
                    min={0}
                    max={totalCount}
                    value={craftCount}
                    onChange={(e) => {
                      const newValue = e.target.valueAsNumber;
                      setState({
                        ...state,
                        craftCountByPath: {
                          ...state.craftCountByPath,
                          [pathId]: newValue,
                        },
                      });
                    }}
                  />
                </div>
              </div>
            );
            path.push(item.id);
            children = (
              <div>
                {renderMaterialsLevel(
                  map,
                  item.recipes[0],
                  craftCount / prodCount,
                  [state, setState],
                  level + 1
                )}
              </div>
            );
            path.pop();
          } else {
            countSelector = (
              <div className={css.recipeCraftCountValues}>
                <div>
                  need:{" "}
                  <span className={css.recipeCraftCountVal}>{totalCount}</span>
                </div>
              </div>
            );
          }

          const isOpen = openByPath[pathId];

          return (
            <div key={mat.itemId}>
              <div className={css.treeHead}>
                <div className={css.treeIconColumn}>
                  <Icon32 icon={item.icon} />
                </div>
                <div className={css.treeMidColumn}>
                  <div className={css.recipeItemName}>
                    <span className={css.recipeItemCount}>{mat.count}</span>
                    <ItemLink item={item} showIcon={false} />
                  </div>
                  {countSelector}
                </div>
                {item.recipes.length > 0 && (
                  <div
                    className={css.levelToggle}
                    onClick={() =>
                      setOpenByPath({ ...openByPath, [pathId]: !isOpen })
                    }
                  >
                    <div className={clsx(css.toggleArrow, isOpen && css.up)} />
                  </div>
                )}
              </div>
              {isOpen && children}
            </div>
          );
        })}
      </div>
    );
  }
}

function parseItemsParam(searchParams: URLSearchParams) {
  const itemsParam = searchParams.get("items") ?? "";
  const itemIds = itemsParam.split("_", 20).map((part) => parseInt(part));
  return itemIds;
}

function encodeRecParam(
  rec: Record<string | number, boolean | number | string>
) {
  return Object.entries(rec)
    .map(([k, v]) => k + "~" + v)
    .join("_");
}

function decodeRecParam<K extends string | number, V>(
  param: string,
  parseKey: (k: string) => K,
  parseValue: (v: string) => V
): Record<K, V> {
  const rec = {} as Record<K, V>;
  for (const pair of param ? param.split("_") : []) {
    const [k, v_] = pair.split("~", 2);
    const v = v_ || "";
    rec[parseKey(k)] = parseValue(v);
  }
  return rec;
}

function convertRecord(record: Record<number, string>): Record<number, number> {
  const result: Record<number, number> = {};

  for (const key in record) {
    if (Object.hasOwnProperty.call(record, key)) {
      const value = record[key];
      // Преобразуем строковое значение в число
      if (value) {
        result[key] = parseInt(value, 10);
      }
    }
  }

  return result;
}

function filterRecord(
  record: Record<number, number>,
  keys: number[]
): Record<number, number> {
  const filteredRecord: Record<number, number> = {};

  for (const key of keys) {
    if (key in record) {
      filteredRecord[key] = record[key];
    }
  }

  return filteredRecord;
}

function normalizeState(
  state: CalcState,
  targets: CraftTarget[],
  data: Map<number, ItemData>
) {
  const cleanState: CalcState = {
    craftCountByPath: {},
    avails: state.avails,
    prices: state.prices,
  };

  const path: number[] = [];

  function process(parentId: number, parentCount: number) {
    if (path.includes(parentId)) {
      // Предотвращаем бесконечную рекурсию
      // console.warn("Циклическая зависимость найдена:", [...path, parentId]);
      return;
    }

    const parent = data.get(parentId);
    path.push(parentId);

    if (parent) {
      for (const mat of parent.recipes[0]?.mats ?? []) {
        const pathId = path.join(".") + "." + mat.itemId;
        const toCraft = Math.min(
          state.craftCountByPath[pathId] ?? 0,
          mat.count * parentCount
        );
        if (toCraft > 0) {
          cleanState.craftCountByPath[pathId] =
            (cleanState.craftCountByPath[pathId] || 0) + toCraft;
        }

        process(mat.itemId, toCraft);
      }
    }

    path.pop();
  }

  for (const target of targets) {
    process(target.item.id, 1);
  }

  return cleanState;
}

function getDescription(props: { realm: Realm }) {
  const { realm } = props;
  const t = tt.bind(null, realm.lang);
  const game = gameName(realm.game);
  return t({
    en: `Craft Calculator ${game} - convenient calculation of the cost of crafting items. A complete list of recipes, with the ability to save prices for resources and materials.`.trim(),
    ru: `Калькулятор Крафта ${game} - удобный расчет стоимости крафта предметов. Полный список рецептов, с возможностью сохранения цен на ресурсы и материалы.`.trim(),
  });
}
